import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  createApiClient,
  type SessionResponse,
} from "@planet-conflict/api-client";
import type { Auth } from "firebase/auth";
import {
  createFirebaseAuth,
  firebaseConfiguration,
  observeIdentity,
  signInWithGoogle,
  signOutFirebase,
} from "./firebase";

export type AuthenticationStatus =
  | "configuring"
  | "unconfigured"
  | "signed-out"
  | "resolving-session"
  | "authenticated"
  | "denied"
  | "unavailable"
  | "error";

export const statusForSessionError = (error: unknown): AuthenticationStatus => {
  if (error instanceof ApiError && error.status === 401) return "signed-out";
  if (error instanceof ApiError && error.status === 403) return "denied";
  return "unavailable";
};

interface AuthenticationState {
  readonly status: AuthenticationStatus;
  readonly session: SessionResponse | null;
  readonly message: string | null;
  readonly signIn: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const AuthenticationContext = createContext<AuthenticationState | null>(null);
const api = createApiClient({
  baseUrl:
    import.meta.env.VITE_API_BASE_URL ?? "https://api.planetconflict.com",
});

const messageForError = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401)
    return "Your Firebase session expired. Sign in again.";
  if (error instanceof ApiError && error.status === 403)
    return "Your identity is valid but does not have access.";
  if (error instanceof ApiError)
    return "The command service could not establish your player session.";
  if (error instanceof Error && error.message.includes("popup-closed-by-user"))
    return "Google sign-in was cancelled.";
  return "Sign-in is temporarily unavailable. No account or game state was changed.";
};

export function AuthenticationProvider({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [auth, setAuth] = useState<Auth | null>(null);
  const [status, setStatus] = useState<AuthenticationStatus>("configuring");
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    void createFirebaseAuth()
      .then((firebaseAuth) => {
        if (!active) return;
        if (!firebaseAuth) {
          setStatus("unconfigured");
          return;
        }
        setAuth(firebaseAuth);
        unsubscribe = observeIdentity(firebaseAuth, (user) => {
          setSession(null);
          setMessage(null);
          if (!user) {
            setStatus("signed-out");
            return;
          }
          setStatus("resolving-session");
          void user
            .getIdToken()
            .then((token) => api.session(token))
            .then((resolved) => {
              if (!active) return;
              setSession(resolved);
              setStatus("authenticated");
            })
            .catch((error: unknown) => {
              if (!active) return;
              setStatus(statusForSessionError(error));
              setMessage(messageForError(error));
            });
        });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setStatus("error");
        setMessage(messageForError(error));
      });
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const signIn = useCallback(async () => {
    if (!auth) {
      setMessage(
        firebaseConfiguration
          ? "Firebase is still initializing."
          : "Firebase configuration is missing.",
      );
      return;
    }
    setMessage(null);
    try {
      await signInWithGoogle(auth);
    } catch (error) {
      setStatus("signed-out");
      setMessage(messageForError(error));
    }
  }, [auth]);

  const signOut = useCallback(async () => {
    if (auth) await signOutFirebase(auth);
    setSession(null);
    setStatus(auth ? "signed-out" : "unconfigured");
  }, [auth]);

  const value = useMemo(
    () => ({ status, session, message, signIn, signOut }),
    [message, session, signIn, signOut, status],
  );
  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export const useAuthentication = () => {
  const value = useContext(AuthenticationContext);
  if (!value)
    throw new Error(
      "useAuthentication must be used inside AuthenticationProvider.",
    );
  return value;
};
